
import { onMounted, reactive, ref, nextTick, computed } from "vue";
import XAtable from "@/components/XATable.vue";
import { workQueryList, workSave, sysUserAdsQueryList } from "@/api/api";
import XDialog from "@/components/XDialog.vue";
import type { FormInstance } from "ant-design-vue";
import { message } from "ant-design-vue";
import { hasPermission } from "@/utils/permission";

interface SearchFormState {
  status: number | undefined;
  type: number | undefined;
  createBy?: string | number;
}

interface FormState {
  id: number | string;
  adsId: string;
  txt: string;
  type: number;
  remark: string;
  status: number;
}

export default {
  components: {
    XAtable,
    XDialog,
  },
  setup() {
    const searchFormState = reactive<SearchFormState>({
      status: undefined,
      type: undefined,
      createBy: undefined,
    });
    const formState = reactive<FormState>({
      id: "",
      adsId: "",
      txt: "",
      type: 0,
      remark: "",
      status: 1,
    });
    const dialogVisible = ref(false);
    const auditLoading = ref(false);
    const pageNo = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const loading = ref(false);
    
    // 基础列配置
    const baseColumns = [
      {
        title: "ID",
        key: "id",
        dataIndex: "id",
      },
      {
        title: "广告账户ID",
        key: "adsId",
        dataIndex: "adsId",
      },
      {
        title: "母BMID",
        key: "bmBase",
        dataIndex: "bmBase",
      },
      {
        title: "指纹ID",
        key: "fingerId",
        dataIndex: "fingerId",
      },
      {
        title: "工单内容",
        key: "txt",
        dataIndex: "txt",
        slots: {
          customRender: "txt",
        },
      },
      {
        title: "类型",
        key: "type",
        dataIndex: "type",
        slots: {
          customRender: "type",
        },
      },
      {
        title: "状态",
        key: "status",
        dataIndex: "status",
        slots: {
          customRender: "status",
        },
      },
      {
        title: "审核意见",
        key: "remark",
        dataIndex: "remark",
        slots: {
          customRender: "remark",
        },
      },
      {
        title: "用户名/ID",
        key: "userId",
        dataIndex: "userId",
        width: 140,
        slots: {
          customRender: "userId",
        },
      },
      {
        title: "创建者(户)",
        key: "createBy",
        dataIndex: "createBy",
        width: 120,
        slots: {
          customRender: "createBy",
        },
      },
      {
        title: "创建时间",
        key: "createTime",
        dataIndex: "createTime",
      },
      {
        title: "更新时间",
        key: "updateTime",
        dataIndex: "updateTime",
      },
    ];

    // 操作列配置
    const operationColumn = {
      title: "操作",
      key: "control",
      dataIndex: "control",
      slots: {
        customRender: "control",
      },
    };

    // 动态计算是否显示操作列
    const hasAnyOperationPermission = computed(() => {
      // 检查是否有审核权限
      return hasPermission('sysWork.list.update');
    });

    // 动态列配置
    const columns = computed(() => {
      const cols = [...baseColumns];
      // 如果有任何操作权限，则显示操作列
      if (hasAnyOperationPermission.value) {
        cols.push(operationColumn);
      }
      return cols;
    });

    const tableData = ref([]);
    const formRef = ref<FormInstance>();

    interface CreatorOption {
      id: string | number;
      name: string;
    }

    const CREATOR_FILTER_PAGE_SIZE = 50;
    const creatorFilterList = ref<CreatorOption[]>([]);
    const creatorFilterLoading = ref(false);
    const creatorFilterPageNo = ref(1);
    const creatorFilterTotal = ref(0);
    const creatorFilterKeyword = ref("");

    const getCreatorFilterList = (reset = false) => {
      if (reset) {
        creatorFilterPageNo.value = 1;
        creatorFilterList.value = [];
      }

      creatorFilterLoading.value = true;
      const isNumeric = /^\d+$/.test(creatorFilterKeyword.value);
      const params: Record<string, string | number> = {
        pageNo: creatorFilterPageNo.value,
        pageSize: CREATOR_FILTER_PAGE_SIZE,
      };
      if (creatorFilterKeyword.value) {
        if (isNumeric) {
          params.id = creatorFilterKeyword.value;
        } else {
          params.name = creatorFilterKeyword.value;
        }
      }

      sysUserAdsQueryList(params)
        .then((res) => {
          creatorFilterLoading.value = false;
          if (res.code === "200" && res.data) {
            creatorFilterTotal.value = res.data.total ?? 0;
            const raw = res.data.records || [];
            const records: CreatorOption[] = raw.map(
              (row: Record<string, unknown>) => ({
                id: (row.id ?? row.userId ?? row.createBy) as string | number,
                name: String(
                  row.name ?? row.userName ?? row.createByName ?? ""
                ),
              })
            );
            if (reset) {
              creatorFilterList.value = records;
            } else {
              creatorFilterList.value = [
                ...creatorFilterList.value,
                ...records,
              ];
            }
          }
        })
        .catch(() => {
          creatorFilterLoading.value = false;
        });
    };

    const handleCreatorFilterSearch = (value: string) => {
      creatorFilterKeyword.value = value || "";
      getCreatorFilterList(true);
    };

    const handleCreatorFilterScroll = (e: any) => {
      const { target } = e;
      if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 10) {
        if (
          creatorFilterList.value.length < creatorFilterTotal.value &&
          !creatorFilterLoading.value
        ) {
          creatorFilterPageNo.value += 1;
          getCreatorFilterList(false);
        }
      }
    };

    const handleCreatorFilterFocus = () => {
      if (creatorFilterList.value.length === 0) {
        creatorFilterKeyword.value = "";
        getCreatorFilterList(true);
      }
    };

    const handleCreatorFilterClear = () => {
      creatorFilterKeyword.value = "";
      getCreatorFilterList(true);
    };

    const search = () => {
      pageNo.value = 1;
      getData();
    };
    const resetForm = () => {
      searchFormState.status = undefined;
      searchFormState.type = undefined;
      searchFormState.createBy = undefined;
      pageNo.value = 1;
      creatorFilterKeyword.value = "";
      creatorFilterPageNo.value = 1;
      creatorFilterList.value = [];
      creatorFilterTotal.value = 0;
      getData();
    };
    const getData = () => {
      loading.value = true;
      var parms: any = {
        pageNo: pageNo.value,
        pageSize: pageSize.value,
      };
      if (searchFormState.status !== undefined) {
        parms.status = searchFormState.status;
      }
      if (searchFormState.type !== undefined) {
        parms.type = searchFormState.type;
      }
      if (
        searchFormState.createBy !== undefined &&
        searchFormState.createBy !== null &&
        searchFormState.createBy !== ""
      ) {
        parms.createBy = searchFormState.createBy;
      }
      workQueryList(parms).then((res) => {
        loading.value = false;
        if (res.code === "200") {
          total.value = res.data.total || 0;
          tableData.value = res.data.records || [];
        }
      });
    };
    const pageNoChange = (val) => {
      pageNo.value = val;
      getData();
    };
    const onShowSizeChange = (current, size) => {
      pageSize.value = size;
      getData();
    };
    const auditClick = async (row: any) => {
      // 先打开弹窗
      dialogVisible.value = true;

      // 等待弹窗渲染完成后再设置值
      await nextTick();

      // 设置表单值
      formState.id = row.id || "";
      formState.adsId = row.adsId || "";
      formState.txt = row.txt || "";
      formState.type = row.type !== undefined ? row.type : 0;
      formState.remark = row.remark || "";
      // 审核时默认状态为"已处理"，如果当前是等待处理状态
      formState.status =
        row.status === 0 ? 1 : row.status !== undefined ? row.status : 1;

      // 清除表单验证状态
      await nextTick();
      formRef.value?.clearValidate();
    };
    const handleCancel = async () => {
      dialogVisible.value = false;
      auditLoading.value = false;
      await nextTick();
      formRef.value?.resetFields();
    };
    const confirm = async () => {
      try {
        const values = await formRef.value.validateFields();
        auditLoading.value = true;

        workSave(formState).then((res) => {
          auditLoading.value = false;
          if (res.code === "200") {
            message.success("审核成功");
            dialogVisible.value = false;
            getData();
          }
        });
      } catch (errorInfo) {
        auditLoading.value = false;
        console.log("Failed:", errorInfo);
      }
    };
    onMounted(() => {
      getData();
    });
    return {
      searchFormState,
      formState,
      dialogVisible,
      auditLoading,
      columns,
      tableData,
      pageNo,
      total,
      loading,
      search,
      resetForm,
      pageNoChange,
      onShowSizeChange,
      auditClick,
      handleCancel,
      confirm,
      formRef,
      creatorFilterList,
      creatorFilterLoading,
      handleCreatorFilterSearch,
      handleCreatorFilterScroll,
      handleCreatorFilterFocus,
      handleCreatorFilterClear,
    };
  },
};
